/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { RefObject } from 'react';
import React from 'react';
import type {
  PickedFilterState,
  OptionKey,
} from '../components/FilterList/filter-hooks';
import { emptyFilterId } from '../components/FilterList/filter-utils';
import { allPhenomenaDict } from '../components/PublicWarningsForm/PhenomenonField';
import type { FormError } from '../store/warningsEventForm/reducer';
import { PublicWarningStatus } from '../store/warningsForm/types';
import type {
  Warning,
  WarningEvent,
  EventWarningForWarningList,
} from '../store/warningsForm/types';

export const ERROR_NOT_FOUND = 'Request failed with status code 400';

export const MOCK_USERNAME = 'user.name';

interface OverflowCheckResult {
  ref: RefObject<HTMLDivElement | null>;
  isOverflowing: boolean;
}

export const getSortedStatus = (
  status: PublicWarningStatus,
): PublicWarningStatus => {
  switch (status) {
    case 'DRAFT_PUBLISHED': {
      return PublicWarningStatus.DRAFT;
    }
    default: {
      return status;
    }
  }
};

export const getWarningWithoutIdStatusEditor = (warning: Warning): Warning => {
  const { id, warningDetail, status, editor, ...fullWarning } = warning;

  const {
    id: warningDetailId,
    linked_to_id: linkedToId,
    proposal_id: proposalId,
    warning_handle_uuid: warningHandleId,
    ...cleanWarningDetail
  } = warningDetail;

  const areas = warningDetail.areas?.map(
    ({ published_warning_uuid, ...area }) => area,
  );

  return {
    ...fullWarning,
    warningDetail: { ...cleanWarningDetail, areas },
  };
};

export const getWarningWithLinkedToId = (warning: Warning): Warning => {
  const { id, warningDetail } = warning;
  const { id: warningDetailId, ...cleanWarningDetail } = warningDetail;

  return {
    ...warning,
    warningDetail: { ...cleanWarningDetail, linked_to_id: id },
    status: PublicWarningStatus.DRAFT_PUBLISHED,
  };
};

export const getWarningWithProposalId = (warning: Warning): Warning => {
  const { id, warningDetail, ...fullWarning } = warning;
  const { id: warningDetailId, ...cleanWarningDetail } = warningDetail;

  return {
    ...fullWarning,
    warningDetail: { ...cleanWarningDetail, proposal_id: id },
  };
};

export default function useOverflowCheck(text: string): OverflowCheckResult {
  const ref = React.useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = React.useState<boolean>(false);

  React.useEffect(() => {
    const checkOverflow = (): void => {
      if (ref.current) {
        setIsOverflowing(ref.current.scrollWidth > ref.current.clientWidth);
      }
    };

    checkOverflow();
  }, [text]);

  return { ref, isOverflowing };
}

export const getTranslationKey = (
  optionId: OptionKey<keyof PickedFilterState>,
  id: string,
): string => {
  if (id === 'source' || id === 'incident' || id === 'area') {
    if (optionId === emptyFilterId) {
      return `warning-unspecified`;
    }
    return optionId;
  }
  const phenomenon = allPhenomenaDict[optionId];

  return phenomenon
    ? phenomenon.translationKey
    : `warning-${id}-${optionId as string}`;
};

export const getIdFromUrl = (url: string): string => {
  const splitString = url?.split('/');
  return url && splitString![splitString.length - 2];
};

export const transformWarningEventsToWarningFormat = (
  warningEventFromBE: WarningEvent,
): EventWarningForWarningList[] => {
  const warningsFromEvent = warningEventFromBE.warnings.map((warning) => {
    return {
      id: warning.id,
      lastUpdatedTime: warningEventFromBE.lastUpdatedTime,
      status: warning.status || warningEventFromBE.status,
      editor: warningEventFromBE.editor,
      warningDetail: {
        descriptionOriginal: '',
        descriptionTranslation: '',
        ...warning,
      },
      type: 'warningsEvent',
      eventId: warningEventFromBE.eventId,
    };
  });

  return warningsFromEvent as EventWarningForWarningList[];
};

export const useScrollTopOnError = (
  error: FormError,
): React.RefObject<HTMLDivElement | null> => {
  const ref = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    if (error?.message && ref.current) {
      ref.current.scrollTop = 0;
    }
  }, [error]);
  return ref;
};

interface FormErrors {
  title: string;
  errors: React.ReactElement[];
}

export const getErrors = (errors: string): null | FormErrors =>
  errors
    ? errors?.split(/\r?\n/).reduce<FormErrors>(
        (allErrors, error, index) => {
          if (index === 0) {
            return {
              ...allErrors,
              title: error,
            };
          }
          const isEven = index % 2 === 0;

          return {
            ...allErrors,
            errors: allErrors.errors.concat(
              // eslint-disable-next-line react/no-array-index-key
              <p key={`error-${index}`} style={{ fontSize: 14 }}>
                {isEven ? error : <i>{error}</i>}
              </p>,
            ),
          };
        },
        { title: '', errors: [] },
      )
    : null;
